/**
 * Tiny pub-sub bus the AppSync wrapper emits to so the debug overlay
 * (apps/web/src/ui/RealtimeDebugOverlay.tsx) can show what's flowing
 * over the WebSocket without coupling the network layer to React.
 *
 * Kept tiny on purpose — every connection / subscribe / frame / close
 * gets one entry. The overlay caps history so memory stays bounded.
 */

export interface RealtimeDebugEntry {
  id: number;
  at: number;
  direction: 'in' | 'out' | 'system';
  channel: string;
  /** Top-level shape — 'connection_init', 'lobby_update', 'open', 'close', etc. */
  type: string;
  payload?: unknown;
}

const MAX_ENTRIES = 200;
let nextId = 1;
const buffer: RealtimeDebugEntry[] = [];
const listeners = new Set<(entries: readonly RealtimeDebugEntry[]) => void>();

function notify() {
  const snapshot = buffer.slice();
  for (const l of listeners) l(snapshot);
}

export function emitRealtimeDebug(entry: Omit<RealtimeDebugEntry, 'id' | 'at'>): void {
  buffer.unshift({ id: nextId++, at: Date.now(), ...entry });
  if (buffer.length > MAX_ENTRIES) buffer.length = MAX_ENTRIES;
  notify();
}

export function subscribeRealtimeDebug(
  cb: (entries: readonly RealtimeDebugEntry[]) => void,
): () => void {
  listeners.add(cb);
  cb(buffer.slice());
  return () => {
    listeners.delete(cb);
  };
}

export function clearRealtimeDebug(): void {
  buffer.length = 0;
  notify();
}
